import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { cloneDeep } from 'lodash-es';
import { MessageService, TreeNode } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DatePickerModule } from 'primeng/datepicker';
import { Dialog } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { TreeSelectModule } from 'primeng/treeselect';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { firstValueFrom, forkJoin } from 'rxjs';
import { afterRender } from '../../../../common/after-render';
import { notifyAndTrackError } from '../../../../common/error';
import { dateToIsoString, isEmpty, isStringEqual, parseLocalDate } from '../../../../common/functions';
import { errorMsg, successMsg, warningMsg } from '../../../../common/messages';
import { aggregateValidation, VALIDATION_PREFIX } from '../../../../common/validation';
import { ProcessingComponent } from '../../../../components';
import { CalendarComponent } from '../../../../components/calendar/calendar.component';
import { RecurrenceScheduleComponent } from '../../../../components/recurrence-schedule/recurrence-schedule.component';
import { LockInitialWidthDirective } from '../../../../directives/lock-initial-width.directive';
import { AppInsightsService } from '../../../../services/app-insights.service';
import {
  ContainerAdminService,
  DictionaryCacheService,
  IDictionaryEntry,
  IOrderItemAddRequest,
  IOrderItemInstanceAddRequest,
  IOrderItemInstanceUpdateRequest,
  IOrderItemType,
  IOrderItemUpdateRequest,
  IRecurrenceSchedule,
  IUnitOfMeasure,
  UnitOfMeasureAdminService
} from '../../../../services/insecta';
import { OrderItemInstanceService } from '../../../../services/insecta/order-item-instance.service';
import { OrderItemTypeService } from '../../../../services/insecta/order-item-type.service';
import { OrderItemService } from '../../../../services/insecta/orderitem.service';
import { ProfileType } from '../../../../services/msgraph.service';
import { SubstrateItemComponent } from '../substrate-item/substrate-item.component';

@Component({
  selector: 'app-order-substrate',
  standalone: true,
  imports: [
    SubstrateItemComponent,
    FloatLabelModule,
    DatePickerModule,
    CommonModule,
    FormsModule,
    ToggleSwitchModule,
    SelectModule,
    TreeSelectModule,
    TooltipModule,
    ButtonModule,
    ProcessingComponent,
    TextareaModule,
    InputNumberModule,
    CardModule,
    Dialog,
    CalendarComponent,
    RecurrenceScheduleComponent,
    LockInitialWidthDirective
  ],
  templateUrl: './order-substrate.component.html'
})
export class OrderSubstrateComponent implements OnInit {
  private readonly orderItemService = inject(OrderItemService);
  private readonly msgService = inject(MessageService);
  private readonly unitService = inject(UnitOfMeasureAdminService);
  private readonly dictionaryCache = inject(DictionaryCacheService);
  private readonly containerService = inject(ContainerAdminService);
  private readonly orderItemTypeService = inject(OrderItemTypeService);
  private readonly orderItemInstanceService = inject(OrderItemInstanceService);
  private readonly appInsights = inject(AppInsightsService);

  @ViewChild('calendarComp') calendarComp!: CalendarComponent;
  @ViewChild('recurrenceComp') recurrenceComp!: RecurrenceScheduleComponent;
  @ViewChild('projectSelect', { read: ElementRef }) projectSelect!: ElementRef;
  @ViewChild('containerSelect', { read: ElementRef }) containerSelect!: ElementRef;

  @Input() orderId!: string;
  @Input() catalogId!: string;
  @Input() locationId!: string;
  @Input() editingOrderItemDetails: any;
  @Input() editing: boolean = false;
  @Input() projects!: any[];
  @Input() catalogInformation!: any[];
  @Input() profile!: ProfileType;
  @Input() editingInstance: boolean = false;
  @Input() editingMode!: string;
  @Input() selectedOrderInstance: any;
  @Input() canApprove: boolean = false;
  @Input() status!: string;
  @Input() modalTitle: string = '';
  @Input() projectTooltipText: string = '';
  @Output() public dialogCloseEvent = new EventEmitter();
  @Output() public dialogCancelEvent = new EventEmitter();
  @Input() locationNodes: TreeNode[] = [];
  selectedLocationNode: TreeNode | null = null;

  showRecurringDialog = false;
  recurringDates: string[] = [];
  invalidRecurringDates: string[] = [];
  invalidDays: number[] = [];
  firstDeliveryDate: Date | null = null;

  orderSubstrate: {
    container: any;
    quantity: any;
    deliveryDate: Date | null;
    recurring: boolean;
    note: any;
    projectId: any;
    substrateItem: any;
    deliveryLocationId?: string | null;
  } = {
      container: null,
      quantity: null,
      deliveryDate: null,
      recurring: false,
      note: null,
      projectId: null,
      substrateItem: {},
      deliveryLocationId: '00000000-0000-0000-0000-000000000000'
    };

  editingOrderItemSubstrate: {
    catalogContainerSubstrateId: string | null;
    standardFillAmount: number | null;
    substrateAmount: number | null;
    substrateAmountUom: string | null;
    substrateQuantity: number | null;
  } = {
      catalogContainerSubstrateId: null,
      standardFillAmount: null,
      substrateAmount: null,
      substrateAmountUom: null,
      substrateQuantity: null
    };

  units: IUnitOfMeasure[] = [];
  containers: any[] = [];
  allContainers: any[] = [];
  allSubstrates: any[] = [];
  filteredContainers: any[] = [];
  filteredSubstrates: any[] = [];
  unavailableDates: Date[] = [];
  unavailableDatesIsoStrings: string[] = [];
  substrateOrderItemType: any = {} as IOrderItemType;
  selectedDates: string[] = [];
  minDate: Date = new Date();
  maxDate: Date = new Date();
  isProcessing = false;
  containerDict: IDictionaryEntry[] = [];
  substrateDict: IDictionaryEntry[] = [];
  allSubstrateLinks: any[] = [];
  substrateSelected: boolean = false;
  recurrenceSchedule: IRecurrenceSchedule | undefined = undefined;
  currentSubstrateId: string | null = null;
  showAddButton = true;
  selectedEditingDetailsOrderInstance: any;
  leadTime: any;
  defaultRecurrenceSchedule: IRecurrenceSchedule | undefined = undefined;
  defaultSelectedDates: string[] = [];
  defaultFirstDeliveryDate: Date | null = null;
  subsetSelectedDates: string[] = [];
  subsetInstances: any[] = [];
  rescheduleInstanceDates: string[] = [];
  showMakeRecurringButton: boolean = false;
  showRecurringDatesTooltip: boolean = false;
  isSubstrateFormValid: boolean = false;

  private findNodeByKey(nodes: TreeNode[], key: string): TreeNode | null {
    for (const node of nodes) {
      if (isStringEqual(node.key!, key)) {
        return node;
      }
      if (node.children && node.children.length > 0) {
        const found = this.findNodeByKey(node.children, key);
        if (found) return found;
      }
    }
    return null;
  }

  onLocationNodeSelect(event: any): void {
    this.orderSubstrate.deliveryLocationId = event.node?.key || null;
  }

  ngOnInit(): void {
    this.isProcessing = true;
    this.substrateOrderItemType = this.catalogInformation[0].OrderItemTypes.find((item: any) => item.OrderItemTypeName === 'Substrate');

    firstValueFrom(
      forkJoin({
        units: this.unitService.getUnitOfMeasuresAll(),
        containerDict: this.dictionaryCache.get('container'),
        substrateDict: this.dictionaryCache.get('substrate')
      })
    )
      .then(({ units, containerDict, substrateDict }) => {

        this.containerDict = containerDict;
        this.substrateDict = substrateDict;
        this.units = units.filter((u: any) => u.isActive);

        this.allSubstrateLinks = this.catalogInformation[0].Substrates || [];
        this.filteredSubstrates = this.substrateDict.filter((substrate: any) =>
          this.allSubstrateLinks.some((s: any) => isStringEqual(s.CatalogId, this.catalogId) && isStringEqual(s.SubstrateId, substrate.id))
        );

        this.filteredContainers = this.containerDict.filter((container: any) =>
          this.allSubstrateLinks.some((s: any) => isStringEqual(s.CatalogId, this.catalogId) && isStringEqual(s.ContainerId, container.id))
        );

        this.filteredSubstrates = this.substrateDict.filter((substrate: any) =>
          this.allSubstrateLinks.some((s: any) => isStringEqual(s.CatalogId, this.catalogId) && isStringEqual(s.SubstrateId, substrate.id))
        );
        this.filteredSubstrates = this.filteredSubstrates.map((substrate: any) => {
          const link = this.allSubstrateLinks.find(
            (s: any) => isStringEqual(s.CatalogId, this.catalogId) && isStringEqual(s.SubstrateId, substrate.id)
          );
          return {
            ...substrate,
            standardFill: link?.StandardVolume,
            standardUnit: link?.StandardVolumeUom,
            containerId: link?.ContainerId
          };
        });

        this.units = units.filter((u: any) => u.isActive);
        this.filteredContainers.forEach((container) => {
          firstValueFrom(this.containerService.getContainer(container.id))
            .then((details) => {
              container.containerTypeName = details.containerType?.name || 'container';
            })
            .catch((err) => {
              notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'OrderSubstrateComponent', action: 'LoadContainerDetails' });
            });
        });

        if (this.editing) {
          if (this.editingInstance) {
            if (isStringEqual(this.editingMode, 'single')) {
              this.showMakeRecurringButton = false;
            } else {
              if (isStringEqual(this.status, 'Approved') || isStringEqual(this.status, 'Updated')) {
                this.showMakeRecurringButton = true;
              }
            }
          } else {
            this.showMakeRecurringButton = true;
          }

          this.showAddButton = false;
          this.selectedEditingDetailsOrderInstance = this.editingInstance
            ? this.editingOrderItemDetails.orderItemInstances.find((inst: any) => isStringEqual(inst.id, this.selectedOrderInstance.id))
            : null;
          this.orderSubstrate.note = this.editingInstance ? this.selectedEditingDetailsOrderInstance.note : this.editingOrderItemDetails.note;
          this.orderSubstrate.projectId = this.editingOrderItemDetails.projectId?.toUpperCase();
          this.orderSubstrate.quantity = this.editingInstance
            ? this.selectedEditingDetailsOrderInstance.substrateQuantity
            : this.editingOrderItemDetails.substrateQuantity;
          const selectedContainerId = this.catalogInformation
            .flatMap((info) => info.Substrates)
            .find((container: any) => isStringEqual(container.CatalogContainerSubstrateId, this.editingOrderItemDetails.catalogContainerSubstrateId));
          this.orderSubstrate.container = selectedContainerId.ContainerId;
          this.orderSubstrate.deliveryLocationId = this.editingInstance ? this.selectedEditingDetailsOrderInstance.deliveryLocationId : this.editingOrderItemDetails.deliveryLocationId || null;
          this.selectedLocationNode = this.findNodeByKey(this.locationNodes, this.orderSubstrate.deliveryLocationId!) || null;
          this.onContainerChange(this.orderSubstrate.container);
        } else {
          this.orderSubstrate.deliveryLocationId = this.locationId;
          this.selectedLocationNode = this.findNodeByKey(this.locationNodes, this.locationId) || null;
          this.showMakeRecurringButton = true;
          this.isProcessing = false;
        }
        this.computeSubstrateValidation(false);
      })
      .catch((err) => {
        console.log(err);
        this.isProcessing = false;
        errorMsg(this.msgService, 'Failed to load required data');
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'OrderSubstrateComponent', action: 'InitData' });
      });
  }

  onContainerChange(selectedContainerId: string) {
    this.filteredSubstrates = this.substrateDict.filter((substrate: any) =>
      this.allSubstrateLinks.some(
        (s: any) =>
          isStringEqual(s.CatalogId, this.catalogId) &&
          isStringEqual(s.SubstrateId, substrate.id) &&
          isStringEqual(s.ContainerId, selectedContainerId)
      )
    );

    if (
      !isEmpty(this.orderSubstrate.substrateItem.substrateId) &&
      !this.filteredSubstrates.some((s) => s.id === this.orderSubstrate.substrateItem.substrateId)
    ) {
      this.orderSubstrate.deliveryDate = null;
      this.currentSubstrateId = null;
      this.orderSubstrate.substrateItem = {};
      this.removeRecurrences();
      this.substrateSelected = false;
    }

    if (this.editing) {
      this.editingOrderItemSubstrate = {
        catalogContainerSubstrateId: this.editingOrderItemDetails.catalogContainerSubstrateId,
        standardFillAmount: this.editingInstance
          ? this.selectedEditingDetailsOrderInstance.standardFillAmount
          : this.editingOrderItemDetails.standardFillAmount,
        substrateAmount: this.editingInstance
          ? this.selectedEditingDetailsOrderInstance.substrateAmount
          : this.editingOrderItemDetails.substrateAmount,
        substrateAmountUom: this.editingInstance
          ? this.selectedEditingDetailsOrderInstance.substrateAmountUom
          : this.editingOrderItemDetails.substrateAmountUom,
        substrateQuantity: this.editingInstance
          ? this.selectedEditingDetailsOrderInstance.substrateQuantity
          : this.editingOrderItemDetails.substrateQuantity
      };
    }
    this.computeSubstrateValidation(false);
  }

  onReceiveSubstrateData(event: any): void {
    const newId = event.substrateId;

    if (this.currentSubstrateId === newId) {
      this.orderSubstrate.substrateItem = cloneDeep(event);
      this.computeSubstrateValidation(false);
      return;
    }

    afterRender(() => {
      this.isProcessing = true;

      this.filteredContainers = this.containerDict.filter((container: any) =>
        this.allSubstrateLinks.some(
          (s: any) => isStringEqual(s.CatalogId, this.catalogId) && isStringEqual(s.ContainerId, container.id) && isStringEqual(s.SubstrateId, newId)
        )
      );

      if (this.orderSubstrate.container && !this.filteredContainers.some((c) => c.id === this.orderSubstrate.container)) {
        this.orderSubstrate.container = null;
      }

      this.removeRecurrences();
      this.substrateSelected = false;
      this.orderSubstrate.deliveryDate = null;
      this.orderSubstrate.substrateItem = cloneDeep(event);
      this.currentSubstrateId = this.orderSubstrate.substrateItem?.substrateId || null;

      if (this.orderSubstrate.substrateItem && this.orderSubstrate.substrateItem.substrateId) {
        firstValueFrom(
          this.orderItemTypeService.getRecurringOrderItemTypesConstraints(
            this.catalogId,
            this.substrateOrderItemType.OrderItemTypeId,
            null,
            null,
            this.orderSubstrate.substrateItem.substrateId
          )
        )
          .then((data) => {
            this.leadTime = data.maxLeadTime;
            const DEFAULT_MIN_DATE = parseLocalDate('2000-01-01');
            const DEFAULT_MAX_DATE = new Date(2100, 11, 31);
            this.minDate = this.canApprove ? DEFAULT_MIN_DATE : data.minDate === null ? DEFAULT_MIN_DATE : parseLocalDate(data.minDate);
            this.maxDate = data.maxDate === null ? DEFAULT_MAX_DATE : parseLocalDate(data.maxDate);
            this.invalidDays = [0, 1, 2, 3, 4, 5, 6].filter((day) => !data.availableDays.includes(day));
            this.unavailableDates = data.unavailableDates.map(parseLocalDate);
            this.unavailableDatesIsoStrings = this.unavailableDates.map((date) => {
              const yyyy = date.getFullYear();
              const mm = String(date.getMonth() + 1).padStart(2, '0');
              const dd = String(date.getDate()).padStart(2, '0');
              return `${yyyy}-${mm}-${dd}`;
            });
          })
          .catch((err) => {
            notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'OrderSubstrateComponent', action: 'LoadRecurringConstraints' });
          })
          .finally(() => {
            if (this.editing) {
              let firstDeliveryDate: Date | null = null;

              if (this.editingInstance) {
                if (this.selectedEditingDetailsOrderInstance && this.selectedEditingDetailsOrderInstance.deliveryDate) {
                  firstDeliveryDate = parseLocalDate(this.selectedEditingDetailsOrderInstance.deliveryDate);
                }
              } else {
                if (this.editingOrderItemDetails && this.editingOrderItemDetails.firstDeliveryDate) {
                  firstDeliveryDate = parseLocalDate(this.editingOrderItemDetails.firstDeliveryDate);
                }
              }

              if (firstDeliveryDate && firstDeliveryDate < this.minDate && !this.canApprove) {
                warningMsg(this.msgService, 'The first delivery date for this Order Item is no longer valid and must be reselected');
                this.orderSubstrate.deliveryDate = null;
                this.selectedDates = [];
                this.recurrenceSchedule = undefined;
              } else {
                this.orderSubstrate.deliveryDate = firstDeliveryDate;
                if (
                  (this.editingOrderItemDetails && this.editingOrderItemDetails.orderItemInstances) ||
                  (this.editingInstance && this.editingMode === 'future' && this.status === 'Updated')
                ) {
                  this.subsetSelectedDates = this.getSubsetSelectedDates();
                  this.selectedDates = this.editingInstance
                    ? this.subsetSelectedDates
                    : this.editingOrderItemDetails.orderItemInstances.map((inst: { deliveryDate: any }) => inst.deliveryDate);
                  this.recurrenceSchedule = this.editingOrderItemDetails.recurrenceSchedule;
                  this.updateShowRecurringDatesTooltip();
                }
              }
              this.editing = false;
            }
            this.substrateSelected = true;
            this.isProcessing = false;
            this.computeSubstrateValidation(false);
          });
      } else {
        this.isProcessing = false;
        this.computeSubstrateValidation(false);
      }
    });
  }

  getSubsetSelectedDates(): string[] {
    if (!this.editingOrderItemDetails || !this.selectedEditingDetailsOrderInstance) {
      return [];
    }

    const selectedSeq = this.selectedEditingDetailsOrderInstance.orderItemInstanceSequence;
    this.subsetInstances = this.editingOrderItemDetails.orderItemInstances.filter((inst: any) => inst.orderItemInstanceSequence >= selectedSeq);
    return this.subsetInstances.map((inst: any) => inst.deliveryDate);
  }

  onDeliveryDateChange() {
    if (!this.editingInstance && this.selectedDates.length < 2) {
      this.selectedDates = this.orderSubstrate.deliveryDate === null ? [] : [dateToIsoString(this.orderSubstrate.deliveryDate)];
    }
    this.computeSubstrateValidation(false);
  }

  getSelectedContainerTypeName(): string {
    if (!this.orderSubstrate.container) {
      return 'Container';
    }
    const containerInfo = this.catalogInformation
      .flatMap((info) => info.Containers)
      .find((c: any) => c.ContainerId === this.orderSubstrate.container);

    if (containerInfo) {
      return containerInfo.ContainerTypeName;
    }
    const link = this.allSubstrateLinks.find((s: any) => s.ContainerId === this.orderSubstrate.container);

    return link?.ContainerTypeName || 'Container';
  }

  onClose() {
    if (this.editing) {
      this.editing = false;
      this.editingInstance = false;
      this.editingMode = '';
    }
    this.dialogCloseEvent.emit(false);
  }

  onCancel() {
    if (this.editing) {
      this.editing = false;
      this.editingInstance = false;
      this.editingMode = '';
    }
    this.dialogCancelEvent.emit(false);
  }

  openMakeRecurringDialog() {
    if (!this.orderSubstrate.substrateItem.catalogContainerSubstrateId) {
      warningMsg(this.msgService, 'Please select a substrate first');
      return;
    }

    this.firstDeliveryDate = this.orderSubstrate.deliveryDate ? new Date(this.orderSubstrate.deliveryDate) : null;
    this.defaultRecurrenceSchedule = cloneDeep(this.recurrenceSchedule);
    this.defaultSelectedDates = cloneDeep(this.selectedDates);
    this.defaultFirstDeliveryDate = cloneDeep(this.firstDeliveryDate);
    this.showRecurringDialog = true;
  }

  onSaveOrderItem() {
    if (!this.computeSubstrateValidation(true)) {
      return;
    }

    const selectedSubstrate = this.allSubstrateLinks.find(
      (s: any) => s.SubstrateId === this.orderSubstrate.substrateItem.substrateId && s.ContainerId === this.orderSubstrate.container
    );
    if (!selectedSubstrate) {
      errorMsg(this.msgService, 'Selected substrate not found.');
      return;
    }

    this.isProcessing = true;
    const orderItem: IOrderItemAddRequest = {
      orderId: this.orderId,
      orderItemTypeId: this.substrateOrderItemType.OrderItemTypeId,
      projectId: this.orderSubstrate.projectId || null,
      firstDeliveryDate: this.orderSubstrate.deliveryDate === null ? '' : dateToIsoString(this.orderSubstrate.deliveryDate),
      note: this.orderSubstrate.note || null,
      actionBy: this.profile.userPrincipalName,
      catalogInsectVariantId: undefined,
      catalogContainerInsectGrowthStageId: undefined,
      insectQuantity: undefined,
      insectPermitId: undefined,
      infestationRateQuantity: undefined,
      catalogContainerSubstrateId: selectedSubstrate.CatalogContainerSubstrateId,
      substrateAmount: Number(
        this.orderSubstrate.substrateItem.standard ? selectedSubstrate.StandardVolume : this.orderSubstrate.substrateItem.customAmount
      ),
      substrateAmountUom:
        (this.orderSubstrate.substrateItem.standard ? selectedSubstrate.StandardVolumeUom : this.orderSubstrate.substrateItem.customUnit) || null,
      substrateQuantity: Number(this.orderSubstrate.quantity),
      recurrenceSchedule: this.recurrenceSchedule,
      standardFillAmount: this.orderSubstrate.substrateItem.standard,
      standardInfestationRate: false,
      instances: this.buildInstanceObject()
    };

    firstValueFrom(this.orderItemService.addOrderItem(this.orderId, orderItem))
      .then(() => {
        this.isProcessing = false;
        successMsg(this.msgService, 'Order item saved successfully');
        this.onClose();
      })
      .catch((err) => {
        this.isProcessing = false;
        errorMsg(this.msgService, 'Failed to save order item');
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'OrderSubstrateComponent', action: 'AddOrderItem' });
      });
  }

  onEditSaveOrderItem() {
    if (!this.computeSubstrateValidation(true)) {
      return;
    }

    const selectedSubstrate = this.allSubstrateLinks.find(
      (s: any) => s.SubstrateId === this.orderSubstrate.substrateItem.substrateId && s.ContainerId === this.orderSubstrate.container
    );
    if (!selectedSubstrate) {
      errorMsg(this.msgService, 'Selected substrate not found');
      return;
    }

    this.isProcessing = true;

    if (!this.editingInstance) {
      const orderItem: IOrderItemUpdateRequest = {
        id: this.editingOrderItemDetails.id,
        orderItemTypeId: this.editingOrderItemDetails.orderItemTypeId,
        projectId: this.orderSubstrate.projectId || null,
        firstDeliveryDate: this.orderSubstrate.deliveryDate === null ? '' : dateToIsoString(this.orderSubstrate.deliveryDate),
        note: this.orderSubstrate.note || null,
        actionBy: this.profile.userPrincipalName,
        catalogInsectVariantId: undefined,
        catalogContainerInsectGrowthStageId: undefined,
        insectQuantity: undefined,
        insectPermitId: undefined,
        infestationRateQuantity: undefined,
        catalogContainerSubstrateId: selectedSubstrate.CatalogContainerSubstrateId,
        substrateAmount: Number(
          this.orderSubstrate.substrateItem.standard ? selectedSubstrate.StandardVolume : this.orderSubstrate.substrateItem.customAmount
        ),
        substrateAmountUom:
          (this.orderSubstrate.substrateItem.standard ? selectedSubstrate.StandardVolumeUom : this.orderSubstrate.substrateItem.customUnit) || null,
        substrateQuantity: Number(this.orderSubstrate.quantity),
        recurrenceSchedule: this.recurrenceSchedule,
        standardFillAmount: this.orderSubstrate.substrateItem.standard,
        standardInfestationRate: false,
        instances: this.buildInstanceObject(),
        deliveryLocationId: this.orderSubstrate.deliveryLocationId || null
      };

      firstValueFrom(this.orderItemService.updateOrderItem(this.editingOrderItemDetails.orderId, orderItem))
        .then(() => {
          this.isProcessing = false;
          successMsg(this.msgService, 'Order item saved successfully');
          this.onClose();
        })
        .catch((err) => {
          this.isProcessing = false;
          errorMsg(this.msgService, 'Failed to save order item');
          notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'OrderSubstrateComponent', action: 'UpdateOrderItem' });
        });
    }
    if (this.editingInstance) {
      this.rescheduleInstanceDates = this.selectedDates && this.selectedDates.length > 0 ? this.selectedDates : [];
      const orderItemInstance: IOrderItemInstanceUpdateRequest = {
        id: this.selectedOrderInstance.id,
        orderItemInstanceSequence: (() => {
          const seq = this.selectedOrderInstance.orderItemInstanceSequence;
          if (typeof seq === 'string') {
            const parts = seq.split('-');
            const last = parts[parts.length - 1];
            return Number(last.replace(/^0+/, ''));
          }
          return seq;
        })(),
        orderId: this.selectedOrderInstance.orderId,
        orderItemId: this.selectedOrderInstance.orderItemId,
        deliveryDate: this.orderSubstrate.deliveryDate === null ? '' : dateToIsoString(this.orderSubstrate.deliveryDate),
        note: this.orderSubstrate.note || null,
        catalogInsectVariantId: undefined,
        catalogContainerInsectGrowthStageId: undefined,
        insectQuantity: undefined,
        insectPermitId: undefined,
        infestationRateQuantity: undefined,
        catalogContainerSubstrateId: selectedSubstrate.CatalogContainerSubstrateId,
        substrateAmount: Number(
          this.orderSubstrate.substrateItem.standard ? selectedSubstrate.StandardVolume : this.orderSubstrate.substrateItem.customAmount
        ),
        substrateAmountUom:
          (this.orderSubstrate.substrateItem.standard ? selectedSubstrate.StandardVolumeUom : this.orderSubstrate.substrateItem.customUnit) || null,
        substrateQuantity: Number(this.orderSubstrate.quantity),
        standardFillAmount: this.orderSubstrate.substrateItem.standard,
        standardInfestationRate: false,
        lastEditableDate: (() => {
          if (!this.orderSubstrate.deliveryDate || !this.leadTime) return undefined;
          const date = new Date(this.orderSubstrate.deliveryDate);
          date.setDate(date.getDate() - (this.leadTime || 0));
          return dateToIsoString(date);
        })(),
        deliveryLocationId: this.orderSubstrate.deliveryLocationId || null
      };
      firstValueFrom(
        this.orderItemInstanceService.updateOrderItemInstance(
          this.editingOrderItemDetails.orderId,
          this.selectedOrderInstance.orderItemId,
          this.selectedOrderInstance.id,
          orderItemInstance,
          this.editingMode,
          this.status,
          this.rescheduleInstanceDates
        )
      )
        .then(() => {
          successMsg(this.msgService, 'Order instance saved successfully');
          this.onClose();
        })
        .catch((err) => {
          this.isProcessing = false;
          notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'OrderSubstrateComponent', action: 'UpdateOrderItemInstance' });
        })
        .finally(() => {
          this.isProcessing = false;
          this.editing = false;
          this.editingInstance = false;
          this.editingMode = '';
        });
    }
  }

  buildInstanceObject(): IOrderItemInstanceAddRequest[] {
    const selectedSubstrate = this.allSubstrateLinks.find(
      (s: any) => s.SubstrateId === this.orderSubstrate.substrateItem.substrateId && s.ContainerId === this.orderSubstrate.container
    );

    const datesToUse =
      this.selectedDates && this.selectedDates.length > 0
        ? this.selectedDates
        : this.orderSubstrate.deliveryDate === null
          ? null
          : [dateToIsoString(this.orderSubstrate.deliveryDate)];

    if (datesToUse === null || datesToUse.length === 0) {
      return [];
    }
    return datesToUse.map((date) => {
      const deliveryDateForCalc = new Date(date);
      const lastEditableDate = new Date(deliveryDateForCalc);
      lastEditableDate.setDate(lastEditableDate.getDate() - (this.leadTime || 0));

      return {
        deliveryDate: date,
        lastEditableDate: dateToIsoString(lastEditableDate),
        note: this.orderSubstrate.note || undefined,
        catalogInsectVariantId: undefined,
        catalogContainerInsectGrowthStageId: undefined,
        insectQuantity: undefined,
        insectPermitId: undefined,
        infestationRateQuantity: undefined,
        catalogContainerSubstrateId: selectedSubstrate.CatalogContainerSubstrateId,
        substrateAmount: Number(
          this.orderSubstrate.substrateItem.standard ? selectedSubstrate.StandardVolume : this.orderSubstrate.substrateItem.customAmount
        ),
        substrateAmountUom:
          (this.orderSubstrate.substrateItem.standard ? selectedSubstrate.StandardVolumeUom : this.orderSubstrate.substrateItem.customUnit) || null,
        substrateQuantity: Number(this.orderSubstrate.quantity),
        standardFillAmount: this.orderSubstrate.substrateItem.standard,
        standardInfestationRate: false,
        vendorDelay: false,
        deliveryLocationId: this.orderSubstrate.deliveryLocationId || null
      };
    });
  }

  updateRecurrences(event: any) {
    this.selectedDates = event.recurrenceDates;
    this.invalidRecurringDates = event.invalidRecurrenceDates;
    if (event.recurrenceSchedule) {
      this.recurrenceSchedule = event.recurrenceSchedule;
    }

    if (this.selectedDates && this.selectedDates.length > 0) {
      this.orderSubstrate.deliveryDate = parseLocalDate(this.selectedDates[0]);
      if (this.recurrenceSchedule && this.recurrenceSchedule.configuration) {
        this.recurrenceSchedule.configuration.customDates = this.selectedDates;
      }
    }
    this.updateShowRecurringDatesTooltip();
    this.computeSubstrateValidation(false);
  }

  computeSubstrateValidation(showToast: boolean): boolean {
    const substrateItem = this.orderSubstrate.substrateItem || {};

    const basicGroupValid =
      !!this.orderSubstrate.container &&
      !!this.orderSubstrate.quantity &&
      !!this.orderSubstrate.deliveryDate &&
      !!substrateItem &&
      !!substrateItem.catalogContainerSubstrateId;

    let customGroupValid = true;
    if (substrateItem && substrateItem.standard === false) {
      if (!substrateItem.customAmount || !substrateItem.customUnit) {
        customGroupValid = false;
      }
    }

    const overallValid = basicGroupValid && customGroupValid;

    if (showToast && !overallValid) {
      let failingRules: { valid: boolean; message: string }[] = [];
      if (!basicGroupValid) {
        failingRules = [
          { valid: !!this.orderSubstrate.container, message: 'Container must be selected.' },
          { valid: !!this.orderSubstrate.quantity, message: 'Quantity is required.' },
          { valid: !!this.orderSubstrate.deliveryDate, message: 'Delivery date is required.' },
          { valid: !!substrateItem.catalogContainerSubstrateId, message: 'Substrate must be selected.' }
        ];
      } else if (!customGroupValid) {
        failingRules = [
          {
            valid: substrateItem.standard !== false || !!substrateItem.customAmount,
            message: 'Custom amount is required when not using standard fill.'
          },
          { valid: substrateItem.standard !== false || !!substrateItem.customUnit, message: 'Custom unit is required when not using standard fill.' }
        ];
      }
      aggregateValidation(this.msgService, failingRules, { prefix: VALIDATION_PREFIX, dedupe: true, silent: false });
    }

    this.isSubstrateFormValid = overallValid;
    return overallValid;
  }

  onDialogShow() {
    afterRender(() => {
      if (this.calendarComp && this.calendarComp.calendarComponent) {
        this.calendarComp.calendarComponent.getApi().updateSize();
      }
    });
  }

  addRecurrences() {
    const instanceCount = this.subsetInstances.length;
    const dateCount = this.selectedDates.length;

    if (instanceCount !== dateCount && this.editingInstance && (isStringEqual(this.status, 'Updated') || isStringEqual(this.status, 'Approved'))) {
      errorMsg(
        this.msgService,
        `The number of instances to edit is ${instanceCount}, but you have selected ${dateCount} dates. The number of selected dates must match the number of instances you are editing.`
      );
    } else if (this.invalidRecurringDates.length > 0) {
      errorMsg(this.msgService, 'Invalid recurrence dates found - please correct them before continuing');
    } else {
      this.showRecurringDialog = false;
      this.updateShowRecurringDatesTooltip();
    }
  }

  removeRecurrences() {
    this.showRecurringDialog = false;
    this.recurrenceComp?.reset();
    this.selectedDates = [];
    this.invalidRecurringDates = [];
    this.firstDeliveryDate = cloneDeep(this.defaultFirstDeliveryDate);
    this.orderSubstrate.deliveryDate = cloneDeep(this.defaultFirstDeliveryDate);
    this.updateShowRecurringDatesTooltip();
    this.computeSubstrateValidation(false);
  }

  cancelRecurrences() {
    this.showRecurringDialog = false;
    this.recurrenceComp?.reset();
    this.selectedDates = cloneDeep(this.defaultSelectedDates);
    this.recurrenceSchedule = cloneDeep(this.defaultRecurrenceSchedule);
    this.firstDeliveryDate = cloneDeep(this.defaultFirstDeliveryDate);
    this.orderSubstrate.deliveryDate = cloneDeep(this.defaultFirstDeliveryDate);
    this.invalidRecurringDates = [];
    this.updateShowRecurringDatesTooltip();
    this.computeSubstrateValidation(false);
  }

  private updateShowRecurringDatesTooltip() {
    if (this.editingInstance) {
      if (isStringEqual(this.editingMode, 'single')) {
        this.showRecurringDatesTooltip = false;
      } else {
        if (isStringEqual(this.status, 'Approved') || isStringEqual(this.status, 'Updated')) {
          this.showRecurringDatesTooltip = (this.selectedDates && this.selectedDates.length > 1) || false;
        }
      }
    } else {
      this.showRecurringDatesTooltip = (this.selectedDates && this.selectedDates.length > 1) || false;
    }
  }

  get recurringDatesTooltip(): string {
    return this.selectedDates && this.selectedDates.length > 0 ? 'Recurring Dates:\n' + this.selectedDates.join('\n') : '';
  }
}
